import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { FaQuestionCircle, FaRegLightbulb } from 'react-icons/fa';
import styles from './Tooltip.module.css';

const Tooltip = ({ info, className = '', placement = 'bottom' }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const hideTimeoutRef = useRef(null);
  const triggerRef = useRef(null);

  const show = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    updatePosition();
    setIsVisible(true);
  };

  const hide = () => {
    hideTimeoutRef.current = setTimeout(() => setIsVisible(false), 120);
  };

  const updatePosition = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const gap = 4;
    const tooltipWidth = 280;
    const tooltipHeightEst = 100;
    const padding = 8;
    let top = 0;
    let left = rect.left + rect.width / 2 - tooltipWidth / 2;
    switch (placement) {
      case 'bottom':
        top = rect.bottom + gap;
        break;
      case 'top':
        top = rect.top - tooltipHeightEst - gap;
        break;
      case 'right':
        top = rect.top + rect.height / 2 - tooltipHeightEst / 2;
        left = rect.right + gap;
        break;
      case 'left':
        top = rect.top + rect.height / 2 - tooltipHeightEst / 2;
        left = rect.left - tooltipWidth - gap;
        break;
      default:
        top = rect.bottom + gap;
    }
    left = Math.max(padding, Math.min(left, window.innerWidth - tooltipWidth - padding));
    top = Math.max(padding, Math.min(top, window.innerHeight - tooltipHeightEst - padding));
    setPosition({ top, left });
  };

  useLayoutEffect(() => {
    if (isVisible && triggerRef.current) {
      updatePosition();
      const handleUpdate = () => updatePosition();
      window.addEventListener('scroll', handleUpdate, true);
      window.addEventListener('resize', handleUpdate);
      return () => {
        window.removeEventListener('scroll', handleUpdate, true);
        window.removeEventListener('resize', handleUpdate);
      };
    }
  }, [isVisible, placement]);

  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    };
  }, []);

  const tooltipElement = isVisible && (
    <div
      className={`${styles.tooltip} ${styles.tooltipPortal} ${styles[`placement-${placement}`]}`}
      role="tooltip"
      style={{ top: position.top, left: position.left }}
      onMouseEnter={show}
      onMouseLeave={hide}
    >
      <div className={styles.tooltipContent}>
        <FaRegLightbulb size={17} className={styles.lightbulbIcon} />
        <p className={styles.tooltipText}>{info}</p>
      </div>
    </div>
  );

  return (
    <>
      <span
        ref={triggerRef}
        className={`${styles.trigger} ${className}`}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        role="button"
        tabIndex={0}
        aria-label="Mostrar ayuda"
      >
        <FaQuestionCircle size={13} className={styles.icon} />
      </span>
      {ReactDOM.createPortal(tooltipElement, document.body)}
    </>
  );
};

Tooltip.propTypes = {
  info: PropTypes.string.isRequired,
  className: PropTypes.string,
  placement: PropTypes.oneOf(['top', 'bottom', 'left', 'right']),
};

export default Tooltip;
